import { Injectable } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { AuthService } from '../services/auth.service';
import { EnvironmentService } from '../services/environment.service';
import { Websocket2Registration } from '../services/websocket2.service';
import { Subject } from 'rxjs';

export type WSServiceUserStatusValue = 'online'|'offline'|'busy'

export class WSServiceUserStatusProfile {
    api:string = null;
    id:string = null;
    name:string = null;
    surname:string = null;
    email:string = null;
    superadmin:boolean = null;

    constructor(fromData?:any) {
        if(fromData != null) {
            for(let i in fromData) {
                if(typeof this[i] != 'undefined') {
                    switch(i) {
                        case 'id': this[i] = fromData[i].toString(); break;
                        case 'superadmin': this[i] = fromData[i] == 1; break;
                        default: this[i] = fromData[i]; break;
                    }
                }
            }
        }
    }
}

export class PgUserStatusIndex { 
    [id:string]: { 
        profile: WSServiceUserStatusProfile, 
        status: WSServiceUserStatusValue
    } 
}

export class WSServiceLocalUserStatusMessage {
    action:'set-status'|'user_status'|'watch_user'|'unwatch_user';
    user?:WSServiceUserStatusProfile|string;
    target?:string;
    value?:WSServiceUserStatusValue;
    content?:{
        id:string,
        profile:WSServiceUserStatusProfile,
        status:WSServiceUserStatusValue
    };
}

@Injectable({
  providedIn: 'root'
})
export class PgUserStatusService {
    constructor(private environmentService:EnvironmentService, private authService:AuthService) {
        this._handleAuthStatusChange()

        this.authService.statusChange.subscribe(() => {
            this._handleAuthStatusChange()
        })
    }

    statusOptions = [
        { value: 'online', text: 'Online' },
        { value: 'busy', text: 'Busy' },
        { value: 'offline', text: 'Offline' },
    ]

    registered = false;

    registeredChange = new Subject<boolean>();

    userStatus:WSServiceUserStatusValue = null;

    userStatusChange = new Subject<WSServiceUserStatusValue>();

    usersStatusIndex:PgUserStatusIndex = {}

    usersStatusIndexChange = new Subject<PgUserStatusIndex>();

    private socket:Socket = null;

    private registration:Websocket2Registration = null;

    /** Local refcount for watch_user / unwatch_user on the shared socket. */
    private _watchRefCount:{ [userId:string]: number } = {}

    private _disposeRegistration() {
        if(this.registration != null) {
            this.registration.unregister();
            this.registration = null;
        }

        this.socket = null;
        this._watchRefCount = {}
    }

    private _handleAuthStatusChange() {
        if(this.authService.isLoggedIn() && this.environmentService.environment.WebSocket != null) {
            if(this.registration != null) {
                return;
            }

            this.socket = new Socket({ 
                url: this.environmentService.environment.WebSocket, 
                options: {} 
            });

            this.registration = new Websocket2Registration(this.socket, 'user-status', async () => {
                let _token = await this.authService.getAuthToken();
                
                return {
                    api: this.environmentService.environment.APIUrl,
                    token: _token
                }
            });

            this.registration.statusChange.subscribe((status) => {
                this.userStatus = null;
                this.userStatusChange.next(this.userStatus);
                this.usersStatusIndex = {}
                this.usersStatusIndexChange.next(this.usersStatusIndex);

                this.registered = status == 'registered';
                this.registeredChange.next(this.registered);

                if(status == 'registered') {
                    for(let _userId in this._watchRefCount) {
                        if(this._watchRefCount[_userId] > 0) {
                            this.registration.send({
                                action: 'watch_user',
                                target: _userId
                            })
                        }
                    }
                }
                else {
                    this._watchRefCount = {}
                }
            })

            this.registration.onMessage.subscribe((data:WSServiceLocalUserStatusMessage) => {
                if(data.action == 'set-status') {
                    let _profile = typeof data.user == 'object' && data.user != null
                        ? new WSServiceUserStatusProfile(data.user)
                        : null;
                    let _id = _profile != null ? _profile.id : (typeof data.user == 'string' ? data.user : null);

                    if(_id != null && this.authService.isLoggedIn() && String(this.authService.user.id) == String(_id)) {
                        this.userStatus = data.value;
                        this.userStatusChange.next(this.userStatus);
                    }

                    if(_id != null && _profile != null) {
                        this.usersStatusIndex[_id] = {
                            profile: _profile,
                            status: data.value
                        }
                        this.usersStatusIndexChange.next(this.usersStatusIndex);
                    }
                }
                else if(data.action == 'user_status' && data.content != null) {
                    let _id = data.target || data.content.id;
                    if(_id == null) return;

                    let _profile = data.content.profile != null
                        ? new WSServiceUserStatusProfile(data.content.profile)
                        : null;

                    this.usersStatusIndex[_id] = {
                        profile: _profile,
                        status: data.content.status
                    }

                    if(this.authService.isLoggedIn() && String(this.authService.user.id) == String(_id)) {
                        this.userStatus = data.content.status;
                        this.userStatusChange.next(this.userStatus);
                    }

                    this.usersStatusIndexChange.next(this.usersStatusIndex);
                }
            })
        }
        else {
            this._disposeRegistration();

            this.registered = false;
            this.registeredChange.next(this.registered);
            this.userStatus = null;
            this.userStatusChange.next(this.userStatus);
            this.usersStatusIndex = {}
            this.usersStatusIndexChange.next(this.usersStatusIndex);
        }
    }

    setUserStatus(status:WSServiceUserStatusValue) {
        if(this.registration == null || this.registration.status != 'registered') return;

        this.registration.send({
            action: 'set-status',
            value: status
        })
    }

    watch(userId:string) {
        if(userId == null || userId == '') return;
        userId = String(userId);

        if(/^guest_/.test(userId)) return;

        let _count = this._watchRefCount[userId] || 0;
        this._watchRefCount[userId] = _count + 1;

        if(_count == 0 && this.registration != null && this.registration.status == 'registered') {
            this.registration.send({
                action: 'watch_user',
                target: userId
            })
        }
    }

    unwatch(userId:string) {
        if(userId == null || userId == '') return;
        userId = String(userId);

        if(/^guest_/.test(userId)) return;

        let _count = this._watchRefCount[userId] || 0;
        if(_count <= 0) return;

        _count--;
        if(_count == 0) {
            delete this._watchRefCount[userId];
            if(this.registration != null && this.registration.status == 'registered') {
                this.registration.send({
                    action: 'unwatch_user',
                    target: userId
                })
            }
        }
        else {
            this._watchRefCount[userId] = _count;
        }
    }

    getEntry(userId:string) {
        if(userId == null) return null;
        return this.usersStatusIndex[String(userId)] || null;
    }

    getStatus(userId:string):WSServiceUserStatusValue {
        let _entry = this.getEntry(userId);
        return _entry != null ? _entry.status : null;
    }

    getProfile(userId:string):WSServiceUserStatusProfile {
        let _entry = this.getEntry(userId);
        return _entry != null ? _entry.profile : null;
    }

    getDisplayName(userId:string):string {
        if(this.authService.isLoggedIn() && String(this.authService.user.id) == String(userId)) {
            return null; // caller may show "you"
        }

        let _profile = this.getProfile(userId);
        if(_profile == null) return null;

        let _retVal = '';
        for(let _prop of ['name', 'surname']) {
            if(_profile[_prop] != null && _profile[_prop] != '') {
                if(_retVal != '') _retVal += ' ';
                _retVal += _profile[_prop];
            }
        }
        return _retVal || null;
    }

    getStatusClass(userId:string):string {
        let _status = this.getStatus(userId);
        if(_status == 'online') return 'success';
        if(_status == 'busy') return 'danger';
        return 'secondary';
    }
}
